/**
 * throttle function
 *
 * Source: https://github.com/simontonsoftware/s-libs/blob/master/projects/micro-dash/src/lib/function/throttle.ts
 * License: MIT
 *
 * Copyright (c) Eric Simonton
 *
 * This code was copied on 2025-06-30 from the repository above.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait = 0,
  { leading = true, trailing = true } = {},
): ((...args: Parameters<T>) => void) & {
  cancel: () => void;
  flush: () => void;
} {
  let tail = 0;
  let nextArgs: Parameters<T> | undefined;
  let timeoutId: any;

  // helpers to save some bytes
  const now = (): number => performance.now();
  const setNewTail = (): void => {
    tail = now() + wait;
  };

  const cancel = (): void => {
    clearTimeout(timeoutId);
    timeoutId = undefined;
    nextArgs = undefined;
    tail = 0;
  };

  const flush = (): void => {
    if (nextArgs) {
      const args = nextArgs;
      cancel();

      setNewTail();
      func(...args);
    }
  };

  const throttled = (...args: Parameters<T>): void => {
    nextArgs = args;
    const delay = Math.max(0, tail - now());
    if (!delay && (leading || timeoutId)) {
      flush();
    } else if (trailing) {
      if (!delay) {
        setNewTail();
      }
      clearTimeout(timeoutId);
      timeoutId = setTimeout(flush, delay || wait);
    }
  };

  return Object.assign(throttled, { cancel, flush });
}
